/**
 * Модальное окно оплаты
 * 
 * Отображает модальное окно с формой оплаты.
 * Условно рендерит PaymentFormGuest для неавторизованных пользователей
 * или PaymentFormAuth для авторизованных пользователей.
 * Адаптивно: на мобильных устройствах занимает 90% ширины экрана.
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { PaymentFormGuest, PaymentDataGuest } from './PaymentFormGuest';
import { PaymentFormAuth, PaymentDataAuth } from './PaymentFormAuth';
import * as paymentApi from '../../../api/payment.api';
import { useAuthenticatedAccounts } from '../../../hooks/useAuthenticatedAccounts';
import type { PaymentPrefill } from '../../../utils/paymentPrefill';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    maxWidth: '600px',
    width: '100%',
    margin: theme.spacing(2),
    // Адаптивность для мобильных устройств
    [theme.breakpoints.down('sm')]: {
      maxWidth: '90%',
      width: '90%',
      margin: theme.spacing(1),
      borderRadius: '8px',
    },
    // Адаптивность для очень маленьких экранов
    [theme.breakpoints.down(400)]: {
      maxWidth: '95%',
      width: '95%',
      margin: theme.spacing(0.5),
    },
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
  position: 'relative',
  overflow: 'visible',
  // Адаптивность для мобильных устройств
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

export interface PaymentModalProps {
  /** Флаг открытия модального окна */
  open: boolean;
  /** Callback при закрытии модального окна */
  onClose: () => void;
  /** Флаг авторизации пользователя */
  isAuthenticated: boolean;
  /** Email пользователя (опционально) */
  userEmail?: string;
  /** Данные предзаполнения формы из URL (ссылка Telegram-бота) */
  prefill?: PaymentPrefill;
}

/**
 * Модальное окно оплаты
 * 
 * Отображает модальное окно с формой оплаты.
 * Условно рендерит:
 * - PaymentFormGuest - для неавторизованных пользователей
 * - PaymentFormAuth - для авторизованных пользователей
 * 
 * Содержит кнопку закрытия (крестик) в правом верхнем углу.
 * После успешной инициации платежа автоматически закрывает модальное окно.
 * Обрабатывает ошибки оплаты с дружелюбными сообщениями.
 * 
 * Адаптивность:
 * - Desktop: ширина 600px
 * - Mobile: ширина 90%
 * 
 * @param open - Флаг открытия модального окна
 * @param onClose - Callback для закрытия модального окна
 * @param isAuthenticated - Флаг авторизации пользователя
 * @param prefill - Данные предзаполнения формы из URL (передаются в формы)
 * 
 * @example
 * ```tsx
 * const [paymentOpen, setPaymentOpen] = useState(false);
 * const { isAuthenticated } = useAuth();
 * 
 * <PaymentModal
 *   open={paymentOpen}
 *   onClose={() => setPaymentOpen(false)}
 *   isAuthenticated={isAuthenticated}
 * />
 * ```
 */
export const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  isAuthenticated,
  userEmail,
  prefill,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Для авторизованных пользователей загружаем счета
  const { accounts: rawAccounts, primaryAccount, isLoading: isLoadingAccounts } = useAuthenticatedAccounts();
  
  // Преобразуем счета в формат, ожидаемый PaymentFormAuth
  const accounts = React.useMemo(() => {
    return rawAccounts.map(acc => ({
      id: acc.id,
      accountNumber: acc.accountNumber,
      phone: acc.phone || '', // Используем телефон из данных счета
      address: acc.address,
      balance: acc.balance,
      paidUntil: acc.paidUntil || null,
      tariffCost: acc.tariffCost,
      isBlocked: acc.isBlocked,
    }));
  }, [rawAccounts]);
  
  // Локальное состояние для выбранного счета
  const [selectedAccountId, setSelectedAccountId] = useState<string>(
    primaryAccount?.id || ''
  );
  
  // Обновляем выбранный счет при изменении primaryAccount
  React.useEffect(() => {
    if (primaryAccount && !selectedAccountId) {
      setSelectedAccountId(primaryAccount.id);
    }
  }, [primaryAccount, selectedAccountId]);

  /**
   * Обработчик оплаты для гостей
   * Обрабатывает ошибки с дружелюбными сообщениями (Требования 9.2, 9.3, 9.4)
   */
  const handleGuestPayment = useCallback(async (data: PaymentDataGuest) => {
    setError(null);
    setIsLoading(true);
    
    try {
      // Счёт уже выбран пользователем в форме (поиск выполняется автоматически
      // при вводе телефона). Генерируем платёж для выбранного счёта.
      const paymentResult = await paymentApi.generatePaymentGuest({
        accountId: data.accountId,
        amount: data.amount,
        email: data.email,
        annual: data.annual,
      });
      
      // Перенаправляем на страницу оплаты
      window.location.href = paymentResult.paymentUrl;
    } catch (err) {
      // Обработка ошибок сети (Требование 9.3)
      if (axios.isAxiosError(err)) {
        if (!err.response) {
          setError('Проблема с подключением. Проверьте интернет и попробуйте снова.');
        } else if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') {
          setError('Превышено время ожидания. Проверьте подключение к интернету.');
        } else {
          // Обработка HTTP ошибок с дружелюбными формулировками (Требование 9.4)
          switch (err.response.status) {
            case 400:
              setError('Некорректные данные платежа. Проверьте правильность ввода.');
              break;
            case 404:
              setError('Лицевой счет не найден. Проверьте номер телефона.');
              break;
            case 422:
              setError('Ошибка валидации данных. Проверьте введенную сумму и номер телефона.');
              break;
            case 429:
              setError('Слишком много запросов. Попробуйте через несколько минут.');
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              setError('Сервер временно недоступен. Попробуйте позже.');
              break;
            default:
              setError('Произошла ошибка при создании платежа. Попробуйте позже.');
          }
        }
      } else if (err instanceof Error) {
        setError(err.message || 'Произошла непредвиденная ошибка. Попробуйте позже.');
      } else {
        setError('Произошла непредвиденная ошибка. Попробуйте позже.');
      }
      
      console.error('Ошибка оплаты (гость):', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Обработчик оплаты для авторизованных пользователей
   * Обрабатывает ошибки с дружелюбными сообщениями (Требования 9.2, 9.3, 9.4)
   */
  const handleAuthPayment = useCallback(async (data: PaymentDataAuth) => {
    setError(null);
    setIsLoading(true);
    
    try {
      // Генерируем платеж
      const paymentResult = await paymentApi.generatePaymentAuth({
        accountId: data.accountId,
        amount: data.amount,
        email: data.email,
        annual: data.annual,
      });
      
      // Перенаправляем на страницу оплаты
      window.location.href = paymentResult.paymentUrl;
    } catch (err) {
      // Обработка ошибок сети (Требование 9.3)
      if (axios.isAxiosError(err)) {
        if (!err.response) {
          setError('Проблема с подключением. Проверьте интернет и попробуйте снова.');
        } else if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') {
          setError('Превышено время ожидания. Проверьте подключение к интернету.');
        } else {
          // Обработка HTTP ошибок с дружелюбными формулировками (Требование 9.4)
          switch (err.response.status) {
            case 400:
              setError('Некорректные данные платежа. Проверьте правильность ввода.');
              break;
            case 401:
              setError('Сессия истекла. Пожалуйста, войдите снова.');
              break;
            case 403:
              setError('Доступ запрещен. Обратитесь в службу поддержки.');
              break;
            case 404:
              setError('Лицевой счет не найден. Обновите страницу и попробуйте снова.');
              break;
            case 422:
              setError('Ошибка валидации данных. Проверьте введенную сумму.');
              break;
            case 429:
              setError('Слишком много запросов. Попробуйте через несколько минут.');
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              setError('Сервер временно недоступен. Попробуйте позже.');
              break;
            default:
              setError('Произошла ошибка при создании платежа. Попробуйте позже.');
          }
        }
      } else if (err instanceof Error) {
        setError(err.message || 'Произошла непредвиденная ошибка. Попробуйте позже.');
      } else {
        setError('Произошла непредвиденная ошибка. Попробуйте позже.');
      }
      
      console.error('Ошибка оплаты (авторизованный):', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="payment-modal-title"
      maxWidth={false}
    >
      <StyledDialogContent>
        {/* Кнопка закрытия */}
        <CloseButton
          aria-label="закрыть"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </CloseButton>

        {/* Условный рендеринг формы оплаты */}
        <Box>
          {!isAuthenticated ? (
            // Форма для неавторизованных пользователей
            <PaymentFormGuest
              onSubmit={handleGuestPayment}
              isLoading={isLoading}
              error={error}
              open={open}
              onClose={onClose}
              prefill={prefill}
            />
          ) : isLoadingAccounts ? (
            // Индикатор загрузки счетов
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress />
            </Box>
          ) : accounts.length === 0 ? (
            // Если счетов нет, показываем форму для гостей
            <PaymentFormGuest
              onSubmit={handleGuestPayment}
              isLoading={isLoading}
              error={error}
              open={open}
              onClose={onClose}
              prefill={prefill}
            />
          ) : (
            // Форма для авторизованных пользователей
            <PaymentFormAuth
              accounts={accounts}
              selectedAccountId={selectedAccountId}
              onAccountChange={setSelectedAccountId}
              onSubmit={handleAuthPayment}
              isLoading={isLoading}
              error={error}
              open={open}
              onClose={onClose}
              userEmail={userEmail}
              prefill={prefill}
            />
          )}
        </Box>
      </StyledDialogContent>
    </StyledDialog>
  );
};
